import { PublishStatus } from '@walkme/types';
import { ConditionsTree, IDependencies } from '../properties';
import { ContentItemType } from '@walkme/client-lib';
import { VisualDesign } from '@wm-mfes/w5g';
import { PERM_TYPES } from '../permissions';

export enum TableKeys {
  DRAFTS = 'drafts',
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  SENT = 'sent',
  ARCHIVED = 'archived',
}

export enum NotificationPublishStatus {
  Draft = 1,
  Published = 2,
  ReadyToDelete = 3,
  Deleted = 4,
  Archived = 5,
  Paused = 101,
}

export interface INotification {
  id?: UniqueId;
  numericId?: number;
  title?: string;
  runtimeTitle?: string;
  runtimeDescription?: string;
  message?: string;
  type?: NotificationTypeEnum;
  visualDesignTemplate?: UniqueId;
  visualDesignData?: IVisualDesignData;
  targetAudience?: ISegment[];
  audiencesIds?: string[];
  endUsersIds?: string[];
  user?: ICCUser;
  activeRecipientsNum?: number;
  allRecipientsNum?: number;
  systemId?: UniqueId;
  dependencies?: IDependencies[];
  publishStates?: IPublishState[];
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface INotificationTimeZone {
  timezoneOffset: number;
}

export const EndRecurranceALL = 'ALL';
export const EndRecurranceNONE = '__NONE__';

export interface INotificationRecurrence {
  dayRules?: {
    daysInWeek?: number[];
    daysInMonth?: number[];
    daysRepeat?: number;
  };
  weekRules?: {
    weeksInMonth?: number[];
    weeksRepeat?: number;
  };
  monthRules?: {
    monthsInYear?: number[];
    monthsRepeat?: number;
  };
  yearRules?: {
    yearsRepeat?: number;
  };
  activePeriod?: {
    type: 'years' | 'months' | 'weeks' | 'days';
    amount: number;
  };
  endRecurrenceButtons?: typeof EndRecurranceALL | typeof EndRecurranceNONE | string[];
}

export interface INotificationScheduling {
  audienceTimeZone?: INotificationTimeZone;
  recurrence?: INotificationRecurrence;
}

export interface IPublishState {
  id?: UniqueId;
  notificationId?: UniqueId;
  environmentId?: UniqueId;
  publishedAt?: Date;
  startAt?: Date;
  endAt?: Date;
  publishStatus?: NotificationPublishStatus;
  scheduling?: INotificationScheduling;
  readonly isPaused?: boolean;
  readonly isDraft?: boolean;
  readonly isActive?: boolean;
  readonly isScheduled?: boolean;
  readonly isOld?: boolean;
}

export interface ICCUserRole {
  id?: number;
  displayName?: string;
  permissions?: { [permissionName: string]: PERM_TYPES };
}

export interface ICCUser {
  id: number;
  label: string;
  email?: string;
  role?: ICCUserRole;
  impersonator?: any;
  isPublicApiUser?: boolean;
  isBackoffice?: boolean;
}

export interface IPublishData {
  publishStatus: PublishStatus;
  isModified: boolean;
}

export interface ISystem {
  id: number;
  guid: string;
  systemTypeKey: 'Web' | 'Workstation';
  featureFlags: { flagName: string }[];
}

export interface ISegment extends Omit<ICCUser, 'id'>, IPublishData {
  id: string;
  color?: string;
  condition?: ConditionsTree;
}
export interface IEnvironment extends Omit<ICCUser, 'id'> {
  id: string;
  path: string;
}

export interface IPlayableItem extends Partial<IPublishData> {
  id: string;
  name: string;
  steps?: {
    name: string;
    id: string;
  }[];
}

export type IPlayableItems = { [key in ContentItemType]?: IPlayableItem[] };

export interface IVisualDesignData {
  VisualDesign?: VisualDesign;
  endUsersIds?: string[];
}

export enum NotificationTypeEnum {
  List = 0,
  PopUp = 1,
  Toast = 2,
}

export type UniqueId = string;

export type PreviewExtraData = {
  ignoreNotificationType?: boolean;
};

export const USER_API_LABEL = 'api';

export const ALL_RECIPIENTS_KEY = 'All Recipients';
export const END_USERS_RECIPIENTS_KEY = 'Recipients';
